/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import apiClient from '../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AIAccountBalance {
  id: number;
  user: number;
  username: string;
  balance: string;           // Decimal as string
  lifetime_credits: string;
  lifetime_debits: string;
  daily_limit: string | null;
  monthly_limit: string | null;
  low_balance_threshold: string;
  is_active: boolean;
  auto_pause_on_zero: boolean;
  is_low_balance: boolean;
  is_zero_balance: boolean;
  created_at: string;
  updated_at: string;
}

export interface AIAccountBalanceUpdate {
  daily_limit?: string | null;
  monthly_limit?: string | null;
  low_balance_threshold?: string;
  auto_pause_on_zero?: boolean;
}

export interface AIBalanceTransaction {
  id: string;
  account: number;
  transaction_type: string;
  transaction_type_display: string;
  amount: string;
  balance_after: string;
  description: string;
  reference_type: string;
  reference_id: string;
  metadata: Record<string, unknown> | null;
  created_by: number | null;
  created_by_username: string | null;
  created_at: string;
}

export interface TransactionFilters {
  transaction_type?: string;
  reference_type?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedTransactions {
  count: number;
  next: string | null;
  previous: string | null;
  results: AIBalanceTransaction[];
}

export interface SpendingSummary {
  balance: string;
  lifetime_credits: string;
  lifetime_debits: string;
  today_spent: number;
  month_spent: number;
  period_spent: number;
  daily_limit: string | null;
  monthly_limit: string | null;
  daily_remaining: number | null;
  monthly_remaining: number | null;
  is_low_balance: boolean;
  is_active: boolean;
}

export interface AICreditPackage {
  id: number;
  name: string;
  credits: string;
  price: string;
  bonus_credits: string;
  total_credits: number;
  description: string;
  is_active: boolean;
  sort_order: number;
}

export interface UsageBreakdown {
  period_days: number;
  systems: Array<{
    system_id: string;
    system_name: string;
    total_cost: number;
    credits_used?: number;
    experts_cost: number;
    compute_cost: number;
    execution_count: number;
  }>;
  experts: Array<{
    expert_id: string;
    expert_name: string;
    total_cost: number;
    credits_used?: number;
    execution_count: number;
  }>;
  agents: Array<{
    agent_id: string;
    agent_name: string;
    total_cost: number;
    credits_used?: number;
    execution_count: number;
  }>;
}

export type CreditUsagePeriodDays = 7 | 30 | 90;

export type CreditUsageDimension = 'systems' | 'assistants' | 'accounts';

export interface CreditUsageAnalyticsFilters {
  period_days: CreditUsagePeriodDays;
  dimension: CreditUsageDimension;
  entity_ids?: string[];
  timezone?: string;
  top_n?: number;
  include_other?: boolean;
}

export interface CreditUsageEntity {
  entity_id: string;
  entity_type: 'system' | 'assistant' | 'account';
  name: string;
  color: string;
  credits_used: number;
  share_percent: number;
  run_count: number;
  average_credits_per_run: number;
  is_selected: boolean;
}

export interface CreditUsageSeriesSegment {
  entity_id: string;
  credits_used: number;
  share_percent: number;
}

export interface CreditUsageSeriesBucket {
  bucket_start: string;
  bucket_label: string;
  total_credits_used: number;
  segments: CreditUsageSeriesSegment[];
  other_credits_used: number;
  unassigned_credits_used: number;
}

export interface CreditUsageDistributionSlice {
  entity_id: string;
  name: string;
  color: string;
  credits_used: number;
  share_percent: number;
}

export interface CreditUsageAnalyticsResponse {
  period: {
    days: CreditUsagePeriodDays;
    bucket_size: 'day';
    start_at: string;
    end_at: string;
    timezone: string;
  };
  metric: 'credits_used';
  dimension: CreditUsageDimension;
  summary: {
    total_credits_used: number;
    average_daily_credits_used: number;
    run_count: number;
    active_entity_count: number;
  };
  entities: CreditUsageEntity[];
  series: CreditUsageSeriesBucket[];
  distribution: CreditUsageDistributionSlice[];
  capabilities: {
    supports_drilldown: boolean;
    supports_accounts_dimension: boolean;
  };
}

export interface CreditUsageDrilldownFilters {
  period_days: CreditUsagePeriodDays;
  parent_dimension: CreditUsageDimension;
  parent_id: string;
  breakdown: 'experts' | 'runs' | 'systems';
  timezone?: string;
}

export interface CreditUsageDrilldownRow {
  expert_id?: string;
  expert_name?: string;
  execution_id?: string;
  started_at?: string;
  credits_used: number;
  model_credits_used?: number;
  compute_credits_used?: number;
  run_count: number;
  average_credits_per_run: number;
  share_percent: number;
}

export interface CreditUsageDrilldownResponse {
  period: {
    days: CreditUsagePeriodDays;
    start_at: string;
    end_at: string;
    timezone: string;
  };
  parent: {
    dimension: CreditUsageDimension;
    id: string;
    name: string;
  };
  breakdown: 'experts' | 'runs' | 'systems';
  summary: {
    total_credits_used: number;
    run_count: number;
  };
  rows: CreditUsageDrilldownRow[];
}

// ---------------------------------------------------------------------------
// Billing WebSocket event types
// ---------------------------------------------------------------------------

export interface BillingBalanceUpdate {
  type: 'balance_updated' | 'low_balance_warning' | 'balance_exhausted';
  balance: string;
  transaction_type?: string;
  amount?: string;
  description?: string;
  threshold?: string;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const BASE = '/ai/expert/api/billing';

export const billingApi = {
  /** Get current user's account balance */
  getBalance: async (): Promise<AIAccountBalance> => {
    const response = await apiClient.get<AIAccountBalance>(`${BASE}/balance/`);
    return response.data;
  },

  /** Update account settings (limits, threshold, auto-pause) */
  updateBalance: async (data: AIAccountBalanceUpdate): Promise<AIAccountBalance> => {
    const response = await apiClient.patch<AIAccountBalance>(`${BASE}/balance/`, data);
    return response.data;
  },

  /** Get paginated transaction history */
  getTransactions: async (filters?: TransactionFilters): Promise<PaginatedTransactions> => {
    const response = await apiClient.get<PaginatedTransactions>(`${BASE}/transactions/`, {
      params: filters,
    });
    return response.data;
  },

  /** Get spending summary */
  getSpendingSummary: async (days?: number): Promise<SpendingSummary> => {
    const response = await apiClient.get<SpendingSummary>(`${BASE}/transactions/summary/`, {
      params: days ? { days } : undefined,
    });
    return response.data;
  },

  /** Admin: add credits to a user */
  addCredits: async (data: { user_id?: number; amount: number; description?: string }): Promise<AIBalanceTransaction> => {
    const response = await apiClient.post<AIBalanceTransaction>(`${BASE}/credits/add/`, data);
    return response.data;
  },

  /** Self-service top-up: add credits to own account */
  topUp: async (data: { amount: number; note?: string }): Promise<{ message: string; transaction: AIBalanceTransaction; new_balance: number }> => {
    const response = await apiClient.post(`${BASE}/credits/topup/`, data);
    return response.data;
  },

  /** Get available credit packages */
  getPackages: async (): Promise<AICreditPackage[]> => {
    const response = await apiClient.get<AICreditPackage[]>(`${BASE}/packages/`);
    return response.data;
  },

  /** Get usage breakdown by system/expert/agent */
  getUsage: async (days?: number): Promise<UsageBreakdown> => {
    const response = await apiClient.get<UsageBreakdown>(`${BASE}/usage/`, {
      params: days ? { days } : undefined,
    });
    return response.data;
  },

  /** Get chart-ready billing usage analytics */
  getUsageAnalytics: async (
    filters: CreditUsageAnalyticsFilters,
  ): Promise<CreditUsageAnalyticsResponse> => {
    const response = await apiClient.get<CreditUsageAnalyticsResponse>(`${BASE}/usage/analytics/`, {
      params: filters,
    });
    return response.data;
  },

  /** Get drill-down billing usage rows for a selected contributor */
  getUsageDrilldown: async (
    filters: CreditUsageDrilldownFilters,
  ): Promise<CreditUsageDrilldownResponse> => {
    const response = await apiClient.get<CreditUsageDrilldownResponse>(`${BASE}/usage/drilldown/`, {
      params: filters,
    });
    return response.data;
  },
};
